import { Box, Flex, Spinner, useDisclosure, VStack } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import ReturnableSchedulesCard from "components/Pages/CreateRFQ/ReturnableSchedulesCard/ReturnableSchedulesCard";
import React, { useContext, useEffect, useRef, useState } from "react";
import tw, { styled } from "twin.macro";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { AddSectionModal } from "components/Pages/CreateRFQ/AddSectionModal";
import { reorderList } from "utils/reorderList";
import { useField } from "formik";
import { Heading } from "components/Heading";
import { Button } from "components/Button";
import { FieldForm } from "components/RFQ/Input";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import { RSFileUploadRFX } from "../../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/ReturnableSchedules/RSFileUploadRFX";
import { RSFileUploadTemplate } from "../../../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/ReturnableSchedules/RSFileUploadTemplate";
import { QUESTION_TYPE_ID } from "newui/features/Rfx/Submission/constants";
import {
	scrollAndFocusOnQuestion,
	scrollAndFocusOnSection,
	sortRemovableAndSortOrderItems,
} from "./utils";
import { showToast } from "utils/helpers";
import FloatingButton from "newui/features/Rfx/components/FloatingButton";
import Tooltip from "components/Tooltip";
const ProjectDetailContainer = styled.div(() => [tw`flex flex-col w-full`]);

const ButtonAddContent = ({
	onOpen,
	setModalType,
	addSectionHandler,
	disableAddQuestion,
	previewRfxHandler,
}) => {
	const onClickAddHandler = (type) => {
		setModalType(type);
		onOpen();
	};

	const onKeyDown = (event, menu) => {
		if (event.key === "Enter") {
			if (menu) {
				event.preventDefault();
				onClickAddHandler(menu);
			} else {
				addSectionHandler();
			}
		}
	};

	return (
		<>
			<Box className="card-shadow z-40 ml-8 flex-col rounded-md bg-white p-2">
				<Button
					type="button"
					btntype="link"
					className={`[transition-duration: 0.18s] relative !flex !w-full cursor-pointer items-center !justify-start gap-2 p-3 transition-all hover:rounded-md hover:bg-utility-blue-light-50 ${disableAddQuestion && "pointer-events-none opacity-50"}`}
					onClick={() => onClickAddHandler("question")}
					onKeyDown={(event) => onKeyDown(event, "question")}
					data-testid="add-question"
				>
					<Icon
						icon="plus-circle"
						style={{ color: "#475467" }}
					/>
					<p
						className="font-roboto font-medium text-secondary-700"
						style={{ fontSize: "14px", marginTop: "1px" }}
					>
						Add Question
					</p>
				</Button>
				<Button
					type="button"
					btntype="link"
					className="[transition-duration: 0.18s] relative !flex !w-full cursor-pointer items-center !justify-start gap-2 p-3 transition-all hover:rounded-md hover:bg-utility-blue-light-50"
					onClick={() => addSectionHandler()}
					onKeyDown={(event) => onKeyDown(event)}
					data-testid="add-section"
				>
					<Icon
						icon="section"
						style={{ color: "#475467" }}
					/>
					<p
						className="font-roboto font-medium text-secondary-700"
						style={{ fontSize: "14px", marginTop: "1px" }}
					>
						Add Section
					</p>
				</Button>
			</Box>
			<FloatingButton
				className="bottom-20"
				position="left"
			>
				<Box
					className="z-40 flex-col rounded-md bg-white p-2 shadow-md"
					style={{ width: "60px" }}
				>
					<VStack
						gap={2}
						align={"start"}
					>
						<Tooltip
							label="Preview RFx"
							placement="right"
						>
							<Button
								type="button"
								btntype="icon-only"
								className={`${disableAddQuestion && "pointer-events-none opacity-50"}`}
								onClick={() => previewRfxHandler()}
								onKeyDown={(event) => onKeyDown(event, "preview")}
								data-testid="preview-rfx"
								size="sm"
							>
								<Icon
									icon="eye-on"
									style={{ color: "#475467" }}
								/>
							</Button>
						</Tooltip>
						<Tooltip
							label="Add Question"
							placement="right"
						>
							<Button
								type="button"
								btntype="icon-only"
								className={`${disableAddQuestion && "pointer-events-none opacity-50"}`}
								onClick={() => onClickAddHandler("question")}
								onKeyDown={(event) => onKeyDown(event, "question")}
								data-testid="add-question"
								size="sm"
							>
								<Icon
									icon="plus-circle"
									style={{ color: "#475467" }}
								/>
							</Button>
						</Tooltip>
						<Tooltip
							label="Add Section"
							placement="right"
						>
							<Button
								type="button"
								btntype="icon-only"
								onClick={() => addSectionHandler()}
								onKeyDown={(event) => onKeyDown(event)}
								data-testid="add-section"
							>
								<Icon
									icon="section"
									style={{ color: "#475467" }}
								/>
							</Button>
						</Tooltip>
					</VStack>
				</Box>
			</FloatingButton>
		</>
	);
};

const QuestionerContainer = styled.div(({ isDisabled }) => [
	!isDisabled ? tw`opacity-100` : tw`opacity-50 pointer-events-none`,
]);

const returnableSchedulesType = [
	{
		label: "Create my own returnable schedules format",
		value: "form",
		icon: "text-input",
	},
	{ label: "Upload Template", value: "file", icon: "template" },
];

const ReturnableSchedule = ({
	titlePage,
	subTitlePage,
	setFieldError,
	setFieldValue,
	isTemplate,
	rfxOrRfxTemplateId,
	previewRfxHandler,
}) => {
	const isLoading = false;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [modalType, setModalType] = useState("section");
	const [, { value: sectionType }] = useField("returnable_schedule_type");
	const { setUploadInProgresses } = useContext(RfxContext);

	// we need this to prevent duplication
	const lastCopiedSection = useRef(null);
	const lastDeletedSection = useRef(null);

	const [, meta, helper] = useField(`sections`);
	const sectionsValue = meta.value;

	useEffect(() => {
		sectionType === "form"
			? setFieldError("returnable_schedule_file", undefined)
			: setFieldError("sections", undefined);

		// eslint-disable-next-line
	}, [sectionType]);

	const addSectionHandler = () => {
		const newSection = {
			id: crypto.randomUUID(),
			name: "",
			description: "",
			isRemove: 0,
			questionnaire: [
				{
					id: crypto.randomUUID(),
					question: "",
					type: QUESTION_TYPE_ID.PARAGRAPH,
					required: 1,
					fields: [""],
					name: "",
					sort_order: 1,
				},
			],
			sort_order: meta.value?.length + 1,
		};

		helper.setValue(
			sortRemovableAndSortOrderItems([...meta.value, newSection])
		);

		scrollAndFocusOnSection(newSection.id);
	};

	const getDeletedSectionData = (deletedSection) => {
		const deletedQuestionnaire = deletedSection.questionnaire?.map(
			(question) => ({ ...question, name: "deleted", isRemove: 1 })
		);

		return {
			...deletedSection,
			name: "deleted",
			description: "deleted",
			questionnaire: deletedQuestionnaire,
			isRemove: 1,
		};
	};

	const deleteSectionsHandler = (index) => {
		if (lastDeletedSection.current === index) {
			return;
		}

		// start copying this section
		lastDeletedSection.current = index;

		const deletedSection = meta.value[index];

		const existingSections = meta.value;

		// if we haven't saved the section being deleted yet, we can just remove it from the results
		const newSections = [];

		// cycle through existing sections
		for (
			let sectionIndex = 0;
			sectionIndex < existingSections.length;
			sectionIndex++
		) {
			if (sectionIndex === index && isNaN(deletedSection.id)) {
				// we can just remove it
				continue;
			}

			// if deleted item, reset the data
			const section =
				sectionIndex === index
					? getDeletedSectionData(deletedSection)
					: { ...existingSections[sectionIndex] };

			// we will update any sort_order as needed before we push
			if (sectionIndex > index) {
				section.sort_order -= 1;
			}

			newSections.push(section);
		}

		helper.setValue(sortRemovableAndSortOrderItems(newSections));
		typeof meta.error !== "string" && helper.setError(undefined);

		// reset after a short delay to allow next deletion
		setTimeout(() => (lastDeletedSection.current = null), 50);

		showToast(
			"You must save progress to delete permanently",
			`Section ${deletedSection.name ? `'${deletedSection.name}' ` : ""}Deleted`,
			true
		);
	};

	const copySectionsHandler = (index) => {
		if (lastCopiedSection.current === index) {
			return;
		}

		// start copying this section
		lastCopiedSection.current = index;

		const existingSections = meta.value;
		const newSections = [];

		// cycle through existing sections
		for (
			let sectionIndex = 0;
			sectionIndex < existingSections.length;
			sectionIndex++
		) {
			// we will update any sort_order as needed before we push
			const section = { ...existingSections[sectionIndex] };
			if (sectionIndex > index) {
				section.sort_order += 1;
			}
			newSections.push(section);
		}

		// we need to copy the section and questions, but ensure we change the ids
		const sectionToCopy = meta.value[index];
		const copyOfSectionQuestionnaire = sectionToCopy?.questionnaire?.map(
			(question) => {
				return { ...question, id: crypto.randomUUID() };
			}
		);
		const newSectionId = crypto.randomUUID();
		newSections.push({
			...sectionToCopy,
			id: newSectionId,
			name: (sectionToCopy.name += " (copy)"),
			questionnaire: copyOfSectionQuestionnaire,
			sort_order: sectionToCopy.sort_order + 1,
		});

		helper.setValue(sortRemovableAndSortOrderItems(newSections));

		// lets bring the copy nicely into view
		scrollAndFocusOnSection(newSectionId);

		// reset after a short delay to allow next copy
		setTimeout(() => (lastCopiedSection.current = null), 50);

		showToast(null, "Section Copied", true);
	};

	const addQuestionHandler = (sectionIndex) => {
		const currentQuestionnaire = sectionsValue[sectionIndex]?.questionnaire;
		const newSortOrder = currentQuestionnaire
			? currentQuestionnaire.length + 1
			: 1;

		const newQuestion = {
			id: crypto.randomUUID(),
			question: "",
			type: QUESTION_TYPE_ID.PARAGRAPH,
			required: 1,
			fields: [],
			isRemove: 0,
			name: "",
			sort_order: newSortOrder,
			uuid: Math.floor(1000 + Math.random() * 9000),
		};

		let oldValue = [...meta.value];

		if (currentQuestionnaire) {
			oldValue[sectionIndex].questionnaire.push(newQuestion);
		} else {
			oldValue[sectionIndex].questionnaire = [newQuestion];
		}

		helper.setValue(sortRemovableAndSortOrderItems(oldValue));

		scrollAndFocusOnQuestion(newQuestion.id);
	};

	const sectionTotalChecker = () => {
		let total = 0;
		for (const section of sectionsValue) {
			if (!section?.isRemove) {
				total++;
			}
		}
		return total;
	};
	const dragSectionHandler = (result) => {
		let value = sectionsValue;
		let valueSetter = (newValue) => (value = newValue);
		reorderList(value, result, valueSetter);
		const reordered = value.map((item, index) => ({
			...item,
			sort_order: index + 1,
		}));
		helper.setValue(reordered);
	};

	return (
		<ProjectDetailContainer>
			<div
				id="subheader-page"
				className="py-8"
			>
				<div
					id="subheader-page-title"
					className="text-xl font-semibold text-primary-900"
				>
					<Heading
						fontSize={"36px"}
						color="#003CE9"
					>
						{" "}
						{titlePage}
					</Heading>
				</div>
				<div
					id="subheader-page-subtitle"
					className="mt-1 font-roboto text-sm text-tertiary-600"
				>
					{subTitlePage}
				</div>
			</div>
			<div>
				<div className="mb-8 space-y-5 rounded-md bg-[#EEF5FFB2] p-8">
					<p className="text-base text-tertiary-600">
						Select an option below to get started:
					</p>
					<FieldForm
						name="returnable_schedule_type"
						type="select"
						isContainImg={true}
						options={returnableSchedulesType}
						placeholder="Select"
						className="!max-w-[440px]"
					/>
				</div>
			</div>
			<div
				id="project-detail-content"
				className="relative"
			>
				{!isLoading ? (
					sectionType !== "file" ? (
						<>
							<QuestionerContainer>
								<DragDropContext
									onDragEnd={dragSectionHandler}
									className="my-10"
								>
									<Droppable
										droppableId="questioner-container"
										direction="vertical"
									>
										{(provided) => (
											<div
												id="questioner-container"
												className="mb-8"
												{...provided.droppableProps}
												ref={provided.innerRef}
											>
												{sectionsValue?.map((section, index) =>
													!section.isRemove ? (
														<React.Fragment key={`section-${section.id}`}>
															<Draggable
																key={`section-${section.id}`}
																draggableId={`section-${section.id}`}
																index={index}
															>
																{(provided) => (
																	<div
																		ref={provided.innerRef}
																		{...provided.draggableProps}
																	>
																		<ReturnableSchedulesCard
																			index={index}
																			dragHandle={provided.dragHandleProps}
																			section={section}
																			deleteSectionsHandler={
																				deleteSectionsHandler
																			}
																			copySectionsHandler={copySectionsHandler}
																			sectionList={meta.value}
																			setFieldError={setFieldError}
																		/>
																	</div>
																)}
															</Draggable>
															{sectionsValue.length !== index + 1 ? (
																<Flex className="my-10 items-center">
																	<div className="w-full border-b border-gray-200 text-gray-200" />
																	<div className="mx-2 w-[300px] text-center font-roboto text-lg font-bold text-primary-900">
																		Section break
																	</div>
																	<div className="w-full border-b border-gray-200 text-gray-200" />
																</Flex>
															) : null}
														</React.Fragment>
													) : null
												)}
												{provided.placeholder}
											</div>
										)}
									</Droppable>
								</DragDropContext>
								<Flex className="-mt-1">
									<ButtonAddContent
										onOpen={onOpen}
										setModalType={setModalType}
										addSectionHandler={addSectionHandler}
										previewRfxHandler={previewRfxHandler}
										disableAddQuestion={!sectionTotalChecker()}
									/>
								</Flex>
								<AddSectionModal
									modalType={modalType}
									sections={sectionsValue}
									type={modalType}
									isOpen={isOpen}
									onClose={onClose}
									addSectionHandler={addSectionHandler}
									addQuestionHandler={addQuestionHandler}
								/>
							</QuestionerContainer>
						</>
					) : !isTemplate ? (
						<RSFileUploadRFX
							setFieldValue={setFieldValue}
							rfqId={rfxOrRfxTemplateId}
							setUploadInProgresses={setUploadInProgresses}
						/>
					) : (
						<RSFileUploadTemplate
							rfxTemplateId={rfxOrRfxTemplateId}
							setFieldValue={setFieldValue}
							setUploadInProgresses={setUploadInProgresses}
						/>
					)
				) : (
					<Spinner
						className="absolute left-1/2 top-[30vh] -translate-x-1/2"
						size="xl"
					/>
				)}
			</div>
		</ProjectDetailContainer>
	);
};

export default ReturnableSchedule;
